import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSButton, {
  KTBSButtonColor,
  KTBSButtonSize,
} from 'general/components/OtherKeenComponents/KTBSButton';
import KTRadio, { KTRadioType } from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import StringUtils from 'general/utils/StringUtils';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const CustomDropdown2Directions = {
  left: 'dropleft',
  up: 'dropup',
  right: 'dropright',
  down: 'dropdown',
};

export const CustomDropdown2Alignments = {
  start: 'dropdown-menu-start',
  end: 'dropdown-menu-end',
};

export const KTBSDropdowAutoCloseBehavior = {
  true: 'true',
  false: 'false',
  inside: 'inside',
  outside: 'outside',
};

CustomDropdown2.propTypes = {
  name: PropTypes.string,
  toggleWrapperClassName: PropTypes.string,
  toggleElement: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  dropdownMenuClassName: PropTypes.string,
  dropdownMenuStyle: PropTypes.object,
  dropdownMenuItems: PropTypes.array,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  direction: PropTypes.oneOf(Object.values(CustomDropdown2Directions)),
  alignment: PropTypes.oneOf(Object.values(CustomDropdown2Alignments)),
  offset: PropTypes.object,
  staticDisplay: PropTypes.bool, // on | off dynamic positioning
  autoCloseBehavior: PropTypes.oneOf(Object.values(KTBSDropdowAutoCloseBehavior)),
  contentEl: PropTypes.element,
  onChange: PropTypes.func,
  text: PropTypes.string,

  enableCheckValid: PropTypes.bool,
  isTouched: PropTypes.bool,
  isValid: PropTypes.bool,
  feedbackText: PropTypes.string,

  searchable: PropTypes.bool,
  titleSearchable: PropTypes.bool,
  footerEl: PropTypes.element,

  inputCheckbox: PropTypes.func,
  handleItemClick: PropTypes.func,

  disabled: PropTypes.bool,
  isDropdownForm: PropTypes.bool,
  id: PropTypes.string,

  liveSearch: PropTypes.bool,
  onTextChanged: PropTypes.func,
  defaultValue: PropTypes.object,
  defaultItem: PropTypes.object,
  allowBackFromDefault: PropTypes.bool,
  appendText: PropTypes.string,
  ignoreConfirmClick: PropTypes.bool,
  subTitleType: PropTypes.string,
  defaultTitle: PropTypes.string,
  isPositionFixed: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
};

CustomDropdown2.defaultProps = {
  name: '',
  toggleWrapperClassName: '',
  toggleElement: <></>,
  dropdownMenuClassName: '',
  dropdownMenuStyle: {},
  dropdownMenuItems: [],
  selectedValue: null,
  direction: CustomDropdown2Directions.down,
  alignment: CustomDropdown2Alignments.start,
  offset: null,
  staticDisplay: true,
  autoCloseBehavior: KTBSDropdowAutoCloseBehavior.true,
  contentEl: null,
  onChange: null,
  text: '',

  enableCheckValid: false,
  isValid: true,
  isTouched: false,
  feedbackText: '',
  searchable: false,
  titleSearchable: false,
  footerEl: null,
  inputCheckbox: null,
  handleItemClick: null,
  disabled: false,
  isDropdownForm: false,
  id: '',
  liveSearch: false,
  onTextChanged: null,
  defaultValue: {},
  defaultItem: {},
  allowBackFromDefault: false,
  appendText: '',
  ignoreConfirmClick: false,
  subTitleType: '',
  defaultTitle: '',
  isPositionFixed: false,
  searchPlaceholder: '',
};

/**
 *
 * @param {{
 * name: string,
 * toggleWrapperClassName: string,
 * toggleElement: element|function,
 * dropdownMenuClassName: string,
 * dropdownMenuStyle: object,
 * dropdownMenuItems: object[],
 * selectedValue: string|number|object,
 * direction: string,
 * alignment: string,
 * offset: {},
 * staticDisplay: boolean,
 * autoCloseBehavior: boolean,
 * contentEl: element,
 * onChange: function,
 * text: string,
 *
 * enableCheckValid: boolean,
 * isValid: boolean,
 * isTouched: boolean,
 * feedbackText: string,
 *
 * searchable: boolean,
 * titleSearchable: boolean,
 * footerEl: element,
 * inputCheckbox:func,
 * handleItemClick:func,
 * disabled:boolean,
 * isDropdownForm:boolean,
 * id: string,
 *
 * liveSearch: boolean,
 * onTextChanged: function,
 * defaultValue: object,
 * defaultItem: object,
 * appendText: string,
 * ignoreConfirmClick: boolean,
 * subTitleType: string,
 * defaultTitle: string,
 *
 * }} props
 * @returns
 */
function CustomDropdown2(props) {
  // MARK: --- Params ---
  const {
    name,
    toggleWrapperClassName,
    toggleElement,
    dropdownMenuClassName,
    dropdownMenuStyle,
    dropdownMenuItems,
    selectedValue,
    direction,
    alignment,
    offset,
    staticDisplay,
    autoCloseBehavior,
    contentEl,
    onChange,
    text,

    enableCheckValid,
    isValid,
    isTouched,
    feedbackText,

    searchable,
    titleSearchable,
    footerEl,
    inputCheckbox,
    handleItemClick,
    disabled,
    isDropdownForm,
    searchFormClassName,
    isOpen,
    id,
    liveSearch,
    onTextChanged,
    getSubItems,
    defaultValue,
    defaultItem,
    allowBackFromDefault,
    appendText,
    ignoreConfirmClick,
    subTitleType,
    dependItem,
    defaultTitle,
    isPositionFixed,
    searchPlaceholder,
  } = props;
  const currentSelectedValue = useRef(selectedValue);
  const showError = enableCheckValid && !isValid && isTouched && !_.isEmpty(feedbackText);
  const [showSubItem, setShowSubItem] = useState(false);
  const currentSelectedItem = useRef(null);

  const [searchText, setSearchText] = useState('');
  const [searchTitleText, setSearchTitleText] = useState('');
  const { t } = useTranslation();
  const resolvedSearchPlaceholder = searchPlaceholder || `${t('TypeToSearch')}...`;
  const [filteredDropdownMenuItems, setFilteredDropdownMenuItems] = useState([]);
  const [filteredDropdownTitle, setFilteredDropdownTitle] = useState([]);
  const [updateTrigger, setUpdateTrigger] = useState(0);

  useEffect(() => {
    let filteredOptions = dropdownMenuItems;
    if (titleSearchable) {
      filteredOptions = _.filter(dropdownMenuItems, (item) => {
        const itemName = StringUtils.removeVietnameseTones(item?.name ?? '').toLowerCase();
        return itemName?.includes(
          StringUtils.removeVietnameseTones(searchTitleText ?? '').toLowerCase()
        );
      });
    }
    setFilteredDropdownTitle(filteredOptions);
  }, [searchTitleText, dropdownMenuItems]);

  useEffect(() => {
    currentSelectedValue.current = selectedValue;
  }, [selectedValue]);
  useEffect(() => {
    if (currentSelectedItem.current && currentSelectedItem.current.role === defaultItem?.role) {
      const isDefaultItem = (item) => {
        const itemId = item?.id;
        const defaultItemId = defaultItem?.item?.id;
        if (itemId !== undefined && itemId !== null &&
          defaultItemId !== undefined && defaultItemId !== null) {
          return itemId.toString() === defaultItemId.toString();
        }
        return JSON.stringify(item) === JSON.stringify(defaultItem?.item);
      };

      if (!searchText?.length) {
        if (!currentSelectedItem.current.subItems?.some(isDefaultItem)) {
          currentSelectedItem.current = {
            ...currentSelectedItem.current,
            subItems: [defaultItem?.item, ...(currentSelectedItem.current.subItems || [])].filter(
              Boolean
            ),
          };
        }
      } else {
        const itemName = StringUtils.removeVietnameseTones(
          defaultItem?.item?.name ?? ''
        ).toLowerCase();
        if (
          itemName?.includes(StringUtils.removeVietnameseTones(searchText ?? '').toLowerCase()) &&
          !currentSelectedItem.current.subItemsSearch?.some(isDefaultItem)
        ) {
          currentSelectedItem.current = {
            ...currentSelectedItem.current,
            subItemsSearch: [
              defaultItem?.item,
              ...(currentSelectedItem.current.subItemsSearch || []),
            ].filter(Boolean),
          };
        }
      }
    }
  }, [defaultItem, searchText, currentSelectedItem.current]);

  useEffect(() => {
    const filteredOptions = searchText?.length
      ? currentSelectedItem.current?.subItemsSearch || []
      : currentSelectedItem.current?.subItems || [];
    setFilteredDropdownMenuItems(filteredOptions);
  }, [searchText, currentSelectedItem.current, updateTrigger]);

  const handleToggleClick = () => {
    currentSelectedValue.current = selectedValue;
    setPage(1);
    setSearchText('');
  };

  const listRef = useRef(null);
  const isFetching = useRef(false);
  const [page, setPage] = useState(1);
  const lastScrollTop = useRef(0);

  const handleScroll = (item) => {
    if (!listRef.current || isFetching.current) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 2) {
      const currentLength = searchText?.length
        ? currentSelectedItem.current?.subItemsSearch?.length || 0
        : currentSelectedItem.current?.subItems?.length || 0;
      const total = searchText?.length
        ? currentSelectedItem.current?.subItemsSearchTotal || 0
        : currentSelectedItem.current?.subItemsTotal || 0;

      if (currentLength < total) {
        isFetching.current = true;
        setPage((prev) => prev + 1);
        getSubItems?.({ ...currentSelectedItem.current, page, q: searchText })
          .then((result) => {
            if (result && result.values && typeof result.total !== 'undefined') {
              const { values, total } = result;
              currentSelectedItem.current = {
                ...currentSelectedItem.current,
                subItems: !searchText?.length ? [...(currentSelectedItem.current?.subItems || []), ...values] : [...(currentSelectedItem.current?.subItems || [])],
                subItemsSearch: searchText?.length ? [...(currentSelectedItem.current?.subItemsSearch || []), ...values] : [...(currentSelectedItem.current?.subItemsSearch || [])],
              };
              setUpdateTrigger((prev) => prev + 1);

              isFetching.current = false;
            } else {
              isFetching.current = false;
            }
          })
          .catch((error) => {
            console.error('Error fetching subitems:', error);
            isFetching.current = false;
          });
      }
    }

    lastScrollTop.current = scrollTop;
  };

  useEffect(() => {
    const element = listRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (element) {
        element.removeEventListener('scroll', handleScroll);
      }
    };
  }, [searchText]);

  useEffect(() => {
    let cancelled = false;
    if (defaultValue?.role && getSubItems) {
      getSubItems(defaultValue)
        .then((result) => {
          if (cancelled) return;
          if (result && result.values && typeof result.total !== 'undefined') {
            const { values, total } = result;

            currentSelectedItem.current = {
              ...defaultValue,
              subItems: values,
              subItemsTotal: total,
            };
            setFilteredDropdownMenuItems(values);
            setShowSubItem(true);
          }
        })
        .catch((error) => {
          if (!cancelled) {
            console.error('Error fetching default subitems:', error);
          }
        });
    }
    return () => {
      cancelled = true;
    };
  }, [defaultValue?.role, defaultValue?.name, dependItem]);

  const renderDropdownMenuItems = useMemo(() => {
    if (filteredDropdownMenuItems.length > 0) {
      return (
        <>
          {filteredDropdownMenuItems.map((item, index) => {
            return (
              <div key={index}>
                {inputCheckbox ? (
                  <div className="d-flex align-items-center ml-7">
                    {inputCheckbox(item)}
                    <a
                      className={`dropdown-item 
                        ${currentSelectedValue.current?.id &&
                          currentSelectedValue.current?.id === item?.id
                          ? 'active'
                          : ''
                        } px-5`}
                      style={{ paddingLeft: 'unset' }}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleItemClick(item);
                      }}
                    >
                      {item.name}
                    </a>
                  </div>
                ) : (
                  <a
                    className={`d-flex align-items-center justify-content-between dropdown-item 
                      ${currentSelectedValue.current?.id &&
                        currentSelectedValue.current?.id === item?.id
                        ? 'active'
                        : ''
                      } px-5`}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (ignoreConfirmClick) {
                        currentSelectedValue.current = item;
                        onChange?.(currentSelectedValue.current);
                        handleCloseDropdown();
                      } else {
                        currentSelectedValue.current = item;
                      }
                    }}
                  >
                    <div className="d-flex flex-column">
                      <span
                        style={{
                          whiteSpace: 'break-spaces',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {item.name}
                      </span>
                      {!!subTitleType && item?.[subTitleType] && (
                        <span
                          style={{
                            ...UIHelper.buildFontStyle('400', '14px', AppColors.gray[600]),
                            whiteSpace: 'break-spaces',
                            overflowWrap: 'break-word',
                            wordBreak: 'break-all',
                          }}
                        >
                          {item[subTitleType]}
                        </span>
                      )}
                    </div>
                    {currentSelectedValue.current?.id && currentSelectedValue.current?.id === item.id && (
                      <i className="fa-regular fa-check text-success" />
                    )}
                  </a>
                )}
                {item.showDivider && <div className="dropdown-divider" />}
              </div>
            );
          })}
          {!ignoreConfirmClick && (
            <div
              className="py-2 px-4 d-flex justify-content-end"
              style={{
                position: 'sticky',
                bottom: 0,
                background: 'white',
                zIndex: 10,
                width: '100%',
              }}
            >
              <KTBSButton
                color={KTBSButtonColor.success}
                title={t('Lưu')}
                size={KTBSButtonSize.large}
                borderRadius={5}
                buttonTitleFontWeight="600"
                onPress={() => onChange?.(currentSelectedValue.current)}
                additionalClassName="col-2"
              />
            </div>
          )}
        </>
      );
    }

    return (
      <div className="mx-auto">
        <div className="d-flex flex-column align-items-center justify-content-center gap-4 h-100 py-3">
          <img src={imgEmptyNoData} style={{ maxWidth: '150px', maxHeight: '150px' }} />
          <div className="d-flex flex-column align-items-center gap-2">
            <span
              style={{
                fontWeight: '700',
                fontSize: Utils.convertToFontSize(14),
                color: AppColors.gray[800],
              }}
            >
              Chưa có dữ liệu hiển thị
            </span>
          </div>
        </div>
      </div>
    );
  }, [filteredDropdownMenuItems, currentSelectedValue.current]);

  const handleCloseDropdown = () => {
    if (Utils.isValid(id)) {
      const dropdownToggle = document
        .getElementById(id)
        ?.querySelector('[data-bs-toggle="dropdown"]');
      if (dropdownToggle) {
        $(dropdownToggle).dropdown('hide');
      }
    }
  };

  return (
    <div id={`${id}`} className={`${!isDropdownForm ? 'd-flex' : ''}`}>
      <div className={`${direction} w-100 ${disabled && 'dropdown-disabled'}`}>
        {/* Toggle element */}
        {staticDisplay ? (
          <div
            className={`${toggleWrapperClassName} ${direction}`}
            data-bs-toggle="dropdown"
            data-bs-display="static"
            aria-expanded="false"
            data-bs-auto-close={autoCloseBehavior}
            onClick={handleToggleClick}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              position: 'relative',
            }}
          >
            {typeof toggleElement === 'function' ? toggleElement({ showError }) : toggleElement}
          </div>
        ) : (
          <div
            className={`${toggleWrapperClassName} ${direction}`}
            data-bs-toggle="dropdown"
            aria-expanded="false"
            data-bs-auto-close={autoCloseBehavior}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              position: 'relative',
            }}
          >
            {typeof toggleElement === 'function' ? toggleElement({ showError }) : toggleElement}
          </div>
        )}

        {/* Dropdown menu */}
        <div
          className={`dropdown-menu ${dropdownMenuClassName} ${alignment} py-0`}
          style={{
            ...dropdownMenuStyle,
            display: disabled ? 'none' : '',
            position: isPositionFixed ? 'fixed' : undefined,
            top: isPositionFixed ? 0 : undefined,
            left: isPositionFixed ? 0 : undefined,
            zIndex: isPositionFixed ? 100 : dropdownMenuStyle?.zIndex,
          }}
          ref={listRef}
          onScroll={handleScroll}
        >
          {!showSubItem && (
            <div className="py-2">
              {defaultTitle?.length ? (
                <div className='py-2 px-4' style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>
                  {defaultTitle}
                </div>
              ) : <></>}
              {titleSearchable && (
                <div className={`${searchFormClassName ? searchFormClassName : 'px-4 py-2'}`}>
                  <KTFormSearch
                    name={`${name}_searchbar`}
                    value={searchText}
                    placeholder={resolvedSearchPlaceholder}
                    onSubmit={(text) => {
                      if (!liveSearch) {
                        setSearchTitleText(text);
                      } else {
                        if (onTextChanged) {
                          onTextChanged(text);
                        }
                      }
                    }}
                  />
                </div>
              )}
              {(!defaultValue?.role || allowBackFromDefault) &&
                filteredDropdownTitle.length > 0 &&
                !showSubItem &&
                filteredDropdownTitle.map((item, index) => {
                  return (
                    <div key={index}>
                      <a
                        className={`d-flex align-items-center justify-content-between dropdown-item 
                          ${currentSelectedValue.current?.id &&
                            currentSelectedValue.current?.id === item?.id
                            ? 'active'
                            : ''
                          } px-5`}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPage(1);
                          if (item?.role !== 'ALL') {
                            if (getSubItems) {
                              getSubItems(item)
                                .then((result) => {
                                  if (
                                    result &&
                                    result.values &&
                                    typeof result.total !== 'undefined'
                                  ) {
                                    const { values, total } = result;
                                    currentSelectedItem.current = {
                                      ...item,
                                      subItems: values,
                                      subItemsTotal: total,
                                    };
                                    setShowSubItem(true);
                                  }
                                })
                                .catch((error) => {
                                  console.error('Error in handling sub items:', error);
                                });
                            }
                          } else {
                            currentSelectedValue.current = item;
                            onChange?.(item);
                            handleCloseDropdown();
                          }
                        }}
                      >
                        <div className="d-flex flex-column">
                          <span>{item.name}</span>
                        </div>

                        {item?.role !== 'ALL'
                          ? (
                            <i className="far fa-angle-right text-success" />
                          )
                          : (
                            <KTRadio
                              name=""
                              label=""
                              type={KTRadioType.outline}
                              additionalClassName="radio-success"
                              onChange={(value) => {
                                currentSelectedValue.current = item;
                                onChange?.(item);
                                handleCloseDropdown();
                              }}
                              checked={currentSelectedValue.current?.role === item?.role}
                            />
                          )}
                      </a>
                    </div>
                  );
                })}
            </div>
          )}

          {showSubItem && (
            <div className="pt-2">
              <div className="position-sticky top-0 bg-white">
                {/* Title */}
                {(!defaultValue?.role || allowBackFromDefault) && (
                  <div className="dropdown-header d-flex align-items-center">
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowSubItem(false);
                      }}
                    >
                      <i
                        style={{ color: AppColors.gray[800] }}
                        className="fas fa-arrow-left mr-3"
                      ></i>
                    </div>
                    {!_.isEmpty(currentSelectedItem?.current?.name) && (
                      <span
                        style={{
                          fontSize: Utils.returnFontSize(16),
                          fontWeight: '600',
                          color: AppColors.gray[800],
                        }}
                      >
                        {currentSelectedItem.current?.name}
                      </span>
                    )}
                  </div>
                )}

                {searchable && (
                  <div className={`${searchFormClassName ? searchFormClassName : 'px-4 py-2'}`}>
                    <KTFormSearch
                      name={`${name}_searchbar`}
                      value={searchText}
                      placeholder={resolvedSearchPlaceholder}
                      onSubmit={(text) => {
                        if (!liveSearch) {
                          if (getSubItems)
                            getSubItems({ ...currentSelectedItem.current, q: text }).then(
                              (result) => {
                                if (
                                  result &&
                                  result.values &&
                                  typeof result.total !== 'undefined'
                                ) {
                                  const { values, total } = result;
                                  currentSelectedItem.current = {
                                    ...currentSelectedItem.current,
                                    subItemsSearch: values,
                                    subItemsSearchTotal: total,
                                  };
                                  setSearchText(text);
                                }
                              }
                            );
                        } else {
                          if (onTextChanged) {
                            onTextChanged(text);
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </div>

              {!_.isEmpty(contentEl) ? (
                <div>{contentEl}</div>
              ) : (
                <div>{renderDropdownMenuItems}</div>
              )}

              {/* footer element */}
              {footerEl && footerEl}
            </div>
          )}
        </div>
      </div>
      {!_.isEmpty(text) && <span className="form-text text-muted">{text}</span>}
    </div>
  );
}

export default CustomDropdown2;
